/**
 * withWorldAxisPreRotation — the character-property display transform, second
 * generation (ADR-017 §3 amended 2026-08-03). The first generation
 * (offset-clip.ts) transported the world axis into the parent's BIND frame
 * once, assuming the played parent stays near bind. MEASURED false across
 * families: under ADR-019 absolute transport a VRM's played shoulder sits a
 * large frame-delta from its identity bind, which rotated the abduction
 * axis until "wider" read NARROWER on VRoids — and even on Tripo the axis
 * came out mostly parallel to the hanging arm (dot(+Z) only 0.37: two thirds
 * of the offset was spending itself as invisible twist).
 *
 * This door conjugates PER KEY through the PLAYED parent frame: for each key
 * time of the target bone's track, the parent's world rotation is
 * FK-composed from the CLIP'S OWN TRACKS (rest locals fill unanimated
 * ancestors; the static wrapper above the root bone contributes as measured
 * at bind), and the offset for that key is
 *
 *   offset(t) = parentWorld(t)⁻¹ ∘ R(axis_world, θ) ∘ parentWorld(t)
 *
 * so the world effect is EXACTLY θ about the requested world axis at every
 * key, whatever frames the family authored. Between keys the conjugation
 * rides normalized-lerp with the keys — exact at keys, second-order small
 * between them on dense retargeted tracks.
 *
 * AN UNTRACKED BONE STILL WEARS ITS OFFSET (Codex P2, #77 — the first
 * generation's cure, ported to this door). Mapping existing tracks only
 * meant a partial/authored clip with no quaternion track for a rotation's
 * bone silently dropped that bone's arm/head-space offset — the slider
 * moved and the arm did not. A missing track is synthesized as the offset
 * applied over the bone's REST local (what the bone shows when nothing
 * animates it), keyed at the played ancestors' key-time union — offset(t)
 * changes exactly where the parent chain moves — or two constant keys when
 * no ancestor is tracked. A near-zero angle synthesizes nothing: an
 * inactive slider must not stomp a pose on an untracked bone.
 *
 * A rotation may opt OUT of synthesis (`untracked: 'skip'`, ADR-030): the
 * head-seating preview previews a BIND property, and a bind property shows
 * only where the bone is driven — the committed armature leaves an undriven
 * Neck at its (reseated) rest, where the flesh reads the authored head. A
 * preview that synthesized motion there would move what the commit does not
 * and snap back on release (Codex P1, #126).
 *
 * The input clip is UNTOUCHED, and kernel ClipData never passes through —
 * the export doors read ClipData, so a display offset cannot leak into a
 * delivered file by construction (unchanged from generation one).
 * Depends on: three, kernel (RigDescriptor + WorldAxisRotation shape).
 * Depended on by: the app viewport's clip display filter; the direction and
 * angle traps.
 */
import { AnimationClip, Quaternion, QuaternionKeyframeTrack, Vector3, type KeyframeTrack } from 'three';
import type { RigDescriptor, WorldAxisRotation } from '@harryhausen/kernel';

interface AncestorSampler {
  /** The bone's local rotation at time t, written into `out`. */
  sample(t: number, out: Quaternion): void;
}

interface PlayedFrameConjugator {
  /** Ancestor chain, root-first, ending at the PARENT of the target bone. */
  readonly chain: readonly string[];
  /** offset(t) = parentWorld(t)⁻¹ ∘ worldRotation ∘ parentWorld(t), into `out`. */
  offsetAt(t: number, worldRotation: Quaternion, out: Quaternion): void;
}

export function withWorldAxisPreRotation(
  clip: AnimationClip,
  rig: RigDescriptor,
  rotations: readonly WorldAxisRotation[],
): AnimationClip {
  const bones = new Map(rig.bones.map((b) => [b.name, b]));
  const trackByBone = new Map<string, QuaternionKeyframeTrack>();
  for (const track of clip.tracks) {
    if (track instanceof QuaternionKeyframeTrack && track.name.endsWith('.quaternion')) {
      trackByBone.set(track.name.slice(0, -'.quaternion'.length), track);
    }
  }

  /** One sampler per ancestor: its clip track interpolated, or its rest local. */
  const samplerFor = (name: string): AncestorSampler => {
    const track = trackByBone.get(name);
    if (track !== undefined) {
      // createInterpolant exists on every KeyframeTrack at runtime (three's
      // own mixer path); the public typings omit it, hence the narrow cast.
      const interpolant = (
        track as unknown as { createInterpolant(result: Float32Array): { evaluate(t: number): ArrayLike<number> } }
      ).createInterpolant(new Float32Array(4));
      return {
        sample(t, out) {
          const v = interpolant.evaluate(t);
          out.set(v[0] as number, v[1] as number, v[2] as number, v[3] as number).normalize();
        },
      };
    }
    const rest = bones.get(name)?.localBind.rotation;
    const constant = rest === undefined ? new Quaternion() : new Quaternion(rest.x, rest.y, rest.z, rest.w).normalize();
    return {
      sample(_t, out) {
        out.copy(constant);
      },
    };
  };

  /** The conjugation machinery for one target bone, or null when the bone is
   * unknown or root-level — no parent frame, nothing to conjugate through. */
  const conjugatorFor = (boneName: string): PlayedFrameConjugator | null => {
    const target = bones.get(boneName);
    if (target === undefined || target.parent === null) return null;
    const chain: string[] = [];
    for (let cursor: string | null = target.parent; cursor !== null; cursor = bones.get(cursor)?.parent ?? null) {
      chain.unshift(cursor);
    }
    // Static scene transform ABOVE the root bone (wrapper nodes): worldBind(root)
    // = wrapper ∘ localBind(root) ⇒ wrapper = worldBind(root) ∘ localBind(root)⁻¹.
    const root = bones.get(chain[0] as string);
    const wrapper = new Quaternion();
    if (root !== undefined) {
      const w = root.worldBind.rotation;
      const l = root.localBind.rotation;
      wrapper
        .set(w.x, w.y, w.z, w.w)
        .normalize()
        .multiply(new Quaternion(l.x, l.y, l.z, l.w).normalize().invert());
    }
    const samplers = chain.map((name) => samplerFor(name));
    const parentWorld = new Quaternion();
    const local = new Quaternion();
    return {
      chain,
      offsetAt(t, worldRotation, out) {
        parentWorld.copy(wrapper);
        for (const sampler of samplers) {
          sampler.sample(t, local);
          parentWorld.multiply(local);
        }
        out.copy(parentWorld).invert().multiply(worldRotation).multiply(parentWorld);
      },
    };
  };

  const worldRotationOf = (rotation: WorldAxisRotation): Quaternion =>
    new Quaternion().setFromAxisAngle(
      new Vector3(rotation.axis.x, rotation.axis.y, rotation.axis.z).normalize(),
      rotation.radians,
    );

  const byBone = new Map(rotations.map((r) => [r.bone, r]));
  const tracks: KeyframeTrack[] = clip.tracks.map((track) => {
    const boneName = track.name.endsWith('.quaternion') ? track.name.slice(0, -'.quaternion'.length) : null;
    const rotation = boneName === null ? undefined : byBone.get(boneName);
    if (rotation === undefined || !(track instanceof QuaternionKeyframeTrack)) return track.clone();

    const conjugator = conjugatorFor(rotation.bone);
    if (conjugator === null) return track.clone();

    const worldRotation = worldRotationOf(rotation);
    const offset = new Quaternion();
    const key = new Quaternion();
    const values = new Float32Array(track.values.length);
    for (let i = 0, k = 0; i + 3 < track.values.length; i += 4, k += 1) {
      const t = track.times[k] as number;
      // offset(t) = parentWorld⁻¹ ∘ R_world ∘ parentWorld, premultiplied onto the key.
      conjugator.offsetAt(t, worldRotation, offset);
      key.set(
        track.values[i] as number,
        track.values[i + 1] as number,
        track.values[i + 2] as number,
        track.values[i + 3] as number,
      );
      key.premultiply(offset).normalize();
      values[i] = key.x;
      values[i + 1] = key.y;
      values[i + 2] = key.z;
      values[i + 3] = key.w;
    }
    return new QuaternionKeyframeTrack(track.name, Array.from(track.times), Array.from(values));
  });

  // Synthesis for rotations whose bone the clip never animates (header §
  // "an untracked bone still wears its offset").
  for (const rotation of rotations) {
    if (trackByBone.has(rotation.bone)) continue;
    if (rotation.untracked === 'skip') continue; // a seating preview: undriven stays authored
    if (Math.abs(rotation.radians) <= 1e-9) continue;
    const conjugator = conjugatorFor(rotation.bone);
    if (conjugator === null) continue;
    const rest = bones.get(rotation.bone)!.localBind.rotation;
    const restLocal = new Quaternion(rest.x, rest.y, rest.z, rest.w).normalize();

    const timeSet = new Set<number>();
    for (const name of conjugator.chain) {
      const ancestor = trackByBone.get(name);
      if (ancestor !== undefined) for (const t of ancestor.times) timeSet.add(t);
    }
    const times = timeSet.size > 0 ? [...timeSet].sort((a, b) => a - b) : [0, clip.duration];

    const worldRotation = worldRotationOf(rotation);
    const offset = new Quaternion();
    const key = new Quaternion();
    const values: number[] = [];
    for (const t of times) {
      conjugator.offsetAt(t, worldRotation, offset);
      key.copy(restLocal).premultiply(offset).normalize();
      values.push(key.x, key.y, key.z, key.w);
    }
    tracks.push(new QuaternionKeyframeTrack(`${rotation.bone}.quaternion`, times, values));
  }

  return new AnimationClip(clip.name, clip.duration, tracks);
}
